import { styled } from 'dripsy';
import React, { Fragment } from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { View, TextInput, Platform } from 'react-native';
import { observer } from 'mobx-react-lite';

import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BaseText } from '../common/BaseText';
import { Button } from '../common/Button';
import { Separator } from '../common/Separator';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { AvatarImage } from '../common/AvatarImage';
import { IconTouchable } from '../common/IconTouchable';
import { useColors, usePostEditor } from '@hooks';
import { usePostsModel } from '@models/Posts/PostsModel';
import { AnalyticsService, ToastService } from '@services';
import { EmojiPicker } from '../views/EmojiPicker';
import { MediaUploadView } from '../views/MediaUploadView';
import { getRegExpResults, parseApiError, passIf } from '@utils';
import { Post } from '@models/Posts/PostModel';
import { SOUNDCLOUD_URL_REGEX } from '@constants';
import { SoundCloudWidget } from '../views/SoundCloudWidget';

const ModalViewContainer = styled(ModalView)({
  py: '$8',
  px: '$8',
  width: 343,
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Title = styled(BaseText)({
  variant: 'text.xlarge',
});

const ViewerContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const FullName = styled(BaseText)({
  variant: 'text.medium',
  color: '$heading800',
  fontWeight: 'semi',
});

const Input = styled(TextInput)({
  minHeight: 120,
  color: '$text400',
  variant: 'text.medium',
  ...Platform.select({
    web: { outlineStyle: 'none' },
    default: {},
  }),
});

const ActionsContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const PostButtonContainer = styled(View)({
  flex: 1,
});

export interface IPostEditorModalParams {
  item: Post;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'PostEditorModal'
>;

export const PostEditorModal = observer(
  ({ modal }: ModalPropsType) => {
    const { t } = useTranslation();
    const viewer = useViewerModel();
    const colors = useColors();
    const posts = usePostsModel();
    const item = modal.getParam('item');
    const {
      text,
      setText,
      isValidPost,
      removeSelectedMedia,
      openPickerToAddMedia,
      isUpdate,
      submitPost,
      inProgress,
    } = usePostEditor(item);

    function closeModal() {
      modal.closeModal();
    }

    async function submit() {
      try {
        await submitPost();

        if (isUpdate) {
          ToastService.showSuccess(
            t('postEditor.successMessageUpdate'),
          );
        } else {
          AnalyticsService.trackPostAdd();

          ToastService.showSuccess(
            t('postEditor.successMessageCreate'),
          );
        }

        closeModal();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function selectEmoji(symbol: string) {
      setText(text + symbol);
    }

    const title = t('postEditor.title', {
      context: isUpdate ? 'update' : 'create',
    });

    const submitText = isUpdate
      ? t('postEditor.update')
      : t('postEditor.post');

    const [widgetUrl] = getRegExpResults(SOUNDCLOUD_URL_REGEX, text);

    return (
      <ModalViewContainer>
        <TitleContainer>
          <Title>{title}</Title>

          <IconTouchable
            onPress={closeModal}
            name="close-light"
            size={24}
          />
        </TitleContainer>

        <Separator />

        <ViewerContainer>
          <AvatarImage
            size={36}
            source={{ uri: viewer.currentUser?.photo.small?.url }}
          />
          <Separator size="$5" vertical />
          <FullName>{viewer.currentUser?.fullName}</FullName>
        </ViewerContainer>

        <Separator />

        <Input
          value={text}
          onChangeText={setText}
          placeholder={t('postEditor.whatsOnYourMind')}
          placeholderTextColor={colors.$text80}
          multiline
          maxLength={1000}
        />

        {widgetUrl && (
          <Fragment>
            <Separator size="$5" />

            <SoundCloudWidget
              width="100%"
              height="60"
              visual={false}
              url={widgetUrl}
            />
          </Fragment>
        )}

        <Separator size="$5" />

        <MediaUploadView
          onRemove={removeSelectedMedia}
          items={posts.uploadMediaList.asArray}
        />

        <Separator size="$8" />

        <ActionsContainer>
          <PostButtonContainer>
            <Button
              disabled={!isValidPost}
              onPress={submit}
              isLoading={inProgress}
            >
              {submitText}
            </Button>
          </PostButtonContainer>

          <Separator vertical />

          <EmojiPicker onSelect={selectEmoji} />

          <Separator vertical />

          <IconTouchable
            name="image-light"
            size={24}
            onPress={openPickerToAddMedia}
            disabled={!posts.uploadMediaList.canAddMore}
            color={passIf(
              !posts.uploadMediaList.canAddMore,
              '$text200',
            )}
          />
        </ActionsContainer>
      </ModalViewContainer>
    );
  },
);
